import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Camera, Eye, EyeOff, Trash2, Plus } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { PageHeader } from '@/components/PageHeader'

interface Photo {
  id: string
  artistId: string
  showId: string
  caption: string | null
  originalUrl: string
  thumbnailUrl: string | null
  published: boolean
  takenAt: string | null
  createdAt: string
}

export function CrewPhotosPage() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [url, setUrl] = useState('')
  const [caption, setCaption] = useState('')
  const [artistId, setArtistId] = useState('')
  const [showId, setShowId] = useState('')

  const { data, isLoading } = useQuery<Photo[], ApiClientError>({
    queryKey: ['crew-photos'],
    queryFn: () => apiFetch('/crew/profile/photos'),
  })

  const addMutation = useMutation({
    mutationFn: (input: { originalUrl: string; caption?: string; artistId: string; showId: string }) =>
      apiFetch('/crew/profile/photos', { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crew-photos'] })
      setShowForm(false)
      setUrl('')
      setCaption('')
    },
  })

  const togglePublishMutation = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      apiFetch(`/crew/profile/photos/${id}`, { method: 'PATCH', body: { published } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crew-photos'] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/crew/profile/photos/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crew-photos'] }),
  })

  function handleAdd(e: FormEvent) {
    e.preventDefault()
    if (!url.trim() || !artistId || !showId) return
    const input: { originalUrl: string; caption?: string; artistId: string; showId: string } = { originalUrl: url, artistId, showId }
    if (caption) input.caption = caption
    addMutation.mutate(input)
  }

  const photos = data ?? []

  return (
    <div>
      <PageHeader
        title="Photos"
        subtitle="Upload and manage photos from your shows"
        actions={
          <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            Add photo
          </button>
        }
      />

      {showForm && (
        <form onSubmit={handleAdd} className="mb-6 space-y-3 rounded-lg border border-ovation-ink/10 bg-white p-4">
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Image URL</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} required
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="https://..." />
          </div>
          <div>
            <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Caption</label>
            <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Optional caption" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Artist ID</label>
              <input type="text" value={artistId} onChange={(e) => setArtistId(e.target.value)} required
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Artist UUID" />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Show ID</label>
              <input type="text" value={showId} onChange={(e) => setShowId(e.target.value)} required
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="Show UUID" />
            </div>
          </div>
          <button type="submit" disabled={addMutation.isPending}
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
            {addMutation.isPending ? 'Uploading...' : 'Add photo'}
          </button>
        </form>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-ovation-ink/20 border-t-ovation-ink" />
        </div>
      ) : photos.length === 0 ? (
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-center text-sm text-ovation-muted">
          <Camera className="mx-auto mb-2 h-8 w-8" />
          No photos yet. Add photos from your shows to build your portfolio.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {photos.map((photo) => (
            <div key={photo.id} className="overflow-hidden rounded-lg border border-ovation-ink/10 bg-white">
              <div className="aspect-video bg-ovation-ink/5">
                <img src={photo.thumbnailUrl ?? photo.originalUrl} alt={photo.caption ?? 'Show photo'}
                  className="h-full w-full object-cover" />
              </div>
              <div className="p-3">
                {photo.caption && <p className="text-sm text-ovation-ink">{photo.caption}</p>}
                <p className="mt-1 text-xs text-ovation-muted">
                  {new Date(photo.createdAt).toLocaleDateString()}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button"
                    onClick={() => togglePublishMutation.mutate({ id: photo.id, published: !photo.published })}
                    className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${
                      photo.published ? 'bg-emerald-100 text-emerald-700' : 'bg-ovation-ink/5 text-ovation-muted'
                    }`}>
                    {photo.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    {photo.published ? 'Published' : 'Private'}
                  </button>
                  <button type="button" onClick={() => deleteMutation.mutate(photo.id)}
                    className="flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50">
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
